import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Smooths bursty SSE token delivery into an even, ChatGPT-style reveal.
 *
 * The pipeline does not emit one word at a time. Chunks arrive in clumps — a
 * long pause while the model thinks, then several sentences at once — so
 * rendering each chunk the moment it lands makes the answer jerk across the
 * screen. Instead every chunk goes into a buffer and a `requestAnimationFrame`
 * loop drains it a few characters per frame.
 *
 * The rate is steered by how fast text is *arriving*, averaged over a long
 * window — deliberately not by how much is queued right now. Draining a
 * fraction of the outstanding backlog each frame is the obvious approach and
 * it is wrong: the backlog decays as the loop drains it, so every chunk is
 * revealed as the same arc — fast while the buffer is full, slow as it empties
 * — giving a speed swing several times over *within each chunk*. That reads as
 * the answer sprinting in the middle and crawling at the edges. Easing only
 * softens the arc; it cannot remove it, because the input is itself a decaying
 * ramp.
 *
 * Tracking arrival instead holds one near-constant speed for the whole answer.
 * The backlog is still consulted, but only as a slow correction that keeps the
 * buffer near `TARGET_BUFFER_FRAMES` — enough to absorb the next gap without
 * letting the reveal drift far behind the stream.
 *
 * Two further details keep it from reading as stop-start:
 *
 * 1. The rate is eased toward its target rather than snapped to it, so even
 *    the correction cannot step the speed.
 * 2. The loop keeps running while the stream is open, idling on an empty
 *    buffer instead of tearing itself down. Restarting cost a frame plus a
 *    React commit on every lull, which is the hitch between sentences.
 */

/**
 * Characters per frame, floor and ceiling. ~60fps, so 1 ch/frame ~= 60 char/s
 * ~= 11 words/min.
 *
 * The ceiling is load-bearing, not a safety valve. Because the reveal paces
 * itself by how fast text arrives, it inherits the backend's speed — and the
 * pipeline can stream far faster than anyone reads, especially on a cached
 * response, which is what makes an answer appear to print in bursts. Capping
 * at 4 ch/frame (~240 char/s, ~44 words/min) puts a hard ceiling on the
 * reveal: a fast backend simply queues up and the reveal stays even. That
 * cushion is what the caret is for.
 *
 * The floor sits close under the ceiling on purpose. A wide floor-to-ceiling
 * gap is itself a source of unevenness — the reveal audibly drops gear during
 * a slow patch — so keeping the band narrow matters as much as the cap does.
 */
const MIN_CHARS_PER_FRAME = 6
const MAX_CHARS_PER_FRAME = 18

/**
 * Frames of arrival history used to estimate stream speed (~0.5s at 60fps).
 */
const ARRIVAL_WINDOW_FRAMES = 30

/**
 * How far the rate moves toward its target each frame (0-1).
 */
const RATE_EASING = 0.2

/**
 * Backlog the reveal aims to keep buffered, in frames' worth of text.
 */
const TARGET_BUFFER_FRAMES = 20


/**
 * Longest tail the reveal will take after the stream closes, in frames (~0.5s).
 */
const MAX_TAIL_FRAMES = 30

export interface StreamingText {
  /** The portion revealed so far — render this, not the raw buffer. */
  text: string
  /** True while revealed text is still catching up to what has arrived. */
  isTyping: boolean
  /** Queue more text for reveal. */
  push: (chunk: string) => void
  /**
   * Tell the reveal no more text is coming. The animation keeps running until
   * the buffer is empty, then settles — this does NOT snap to the end.
   */
  end: () => void
  /** Reveal everything immediately — call on abort or error. */
  flush: () => void
  /** Drop all text and pending buffer. */
  reset: () => void
}

export function useStreamingText(): StreamingText {
  const [text, setText] = useState('')
  const [isTyping, setIsTyping] = useState(false)

  /** Full text received so far; `shown` is how much of it is on screen. */
  const fullRef = useRef('')
  const shownRef = useRef(0)
  const frameRef = useRef<number | null>(null)
  /** Fractional carry, so slow rates still advance between frames. */
  const carryRef = useRef(0)
  /** Current reveal speed, carried between frames. */
  const rateRef = useRef(MIN_CHARS_PER_FRAME)
  /** True once the producer has said the stream is closed. */
  const endedRef = useRef(false)

  /**
   * Characters that arrived in each of the last `ARRIVAL_WINDOW_FRAMES`
   * frames, as a ring buffer, plus its running total. A ring rather than an
   * array shift: this is touched every frame.
   */
  const arrivalsRef = useRef<Float64Array>(new Float64Array(ARRIVAL_WINDOW_FRAMES))
  const arrivalIdxRef = useRef(0)
  const arrivalSumRef = useRef(0)
  /**
   * Frames of history actually recorded, capped at the window size.
   *
   * The average must divide by this and not by the window length: dividing by
   * a window that has not filled yet reports a fraction of the true arrival
   * rate, so the reveal starts far too slow and visibly accelerates over the
   * first second and a half as the buffer fills.
   */
  const arrivalCountRef = useRef(0)
  /** Characters pushed since the last frame, folded into the ring on tick. */
  const sinceFrameRef = useRef(0)

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
  }, [])

  const clearArrivals = useCallback(() => {
    arrivalsRef.current.fill(0)
    arrivalIdxRef.current = 0
    arrivalSumRef.current = 0
    arrivalCountRef.current = 0
    sinceFrameRef.current = 0
  }, [])

  const settle = useCallback(() => {
    frameRef.current = null
    carryRef.current = 0
    rateRef.current = MIN_CHARS_PER_FRAME
    setIsTyping(false)
  }, [])

  const tick = useCallback(() => {
    // Fold this frame's arrivals into the ring, evicting the oldest sample.
    const ring = arrivalsRef.current
    const i = arrivalIdxRef.current
    arrivalSumRef.current += sinceFrameRef.current - ring[i]
    ring[i] = sinceFrameRef.current
    arrivalIdxRef.current = (i + 1) % ARRIVAL_WINDOW_FRAMES
    if (arrivalCountRef.current < ARRIVAL_WINDOW_FRAMES) arrivalCountRef.current += 1
    sinceFrameRef.current = 0

    const pending = fullRef.current.length - shownRef.current

    if (pending <= 0) {
      // If the stream has ended, we are genuinely done draining the text.
      if (endedRef.current) {
        settle()
        return
      }
      // Otherwise this is a pause between SSE chunks from the LLM.
      // Keep idling without tearing down or declaring typing finished.
      frameRef.current = requestAnimationFrame(tick)
      return
    }

    // Average characters per frame arriving over the window. Once the stream
    // has closed there is no more arrival to track, so the remaining backlog
    // is paced out over the buffer window instead.
    const avgArrival =
      arrivalSumRef.current / Math.max(1, arrivalCountRef.current)

    let target: number
    if (endedRef.current) {
      // The stream has closed but text is still queued — usually several
      // seconds of it, since the reveal runs well behind the wire. Hold the
      // speed the answer has been reading at rather than switching to a
      // backlog-driven formula: pacing the remainder by `pending` makes the
      // tail lurch faster and then decay to a crawl as the queue empties,
      // which is the single most visible speed change in the whole reveal.
      // The one exception is a backlog too large to clear at the current
      // speed. Easing toward a rate that would finish within roughly
      // MAX_TAIL_FRAMES keeps the ending from dragging, and because it is a
      // floor rather than a formula it only ever raises the speed, and only
      // when the tail would otherwise be long.
      target = Math.max(rateRef.current, pending / MAX_TAIL_FRAMES)
    } else {
      // Match arrival, plus a gentle pull toward the target buffer depth.
      // Dividing the error by the window makes it a trickle: a backlog twice
      // the target adds only ~1 char/frame, so it corrects over seconds
      // rather than stepping the speed.
      const desiredBacklog = avgArrival * TARGET_BUFFER_FRAMES
      target = avgArrival + (pending - desiredBacklog) / (TARGET_BUFFER_FRAMES * 4)
    }
    // The cap applies after the close too. Lifting it to clear a deep queue
    // faster was tried and is worse: it produces a visible speed-up partway
    // through the reveal, which is the one artifact this whole pacing scheme
    // exists to avoid. A slightly longer tail is the better trade.
    target = Math.min(MAX_CHARS_PER_FRAME, Math.max(MIN_CHARS_PER_FRAME, target))

    rateRef.current += (target - rateRef.current) * RATE_EASING
    rateRef.current = Math.min(
      MAX_CHARS_PER_FRAME,
      Math.max(MIN_CHARS_PER_FRAME, rateRef.current)
    )

    carryRef.current += rateRef.current
    const step = Math.floor(carryRef.current)

    if (step > 0) {
      carryRef.current -= step
      shownRef.current = Math.min(fullRef.current.length, shownRef.current + step)
      setText(fullRef.current.slice(0, shownRef.current))
    }

    frameRef.current = requestAnimationFrame(tick)
  }, [settle])

  const start = useCallback(() => {
    if (frameRef.current === null) {
      setIsTyping(true)
      frameRef.current = requestAnimationFrame(tick)
    }
  }, [tick])

  const push = useCallback(
    (chunk: string) => {
      if (!chunk) return
      endedRef.current = false
      fullRef.current += chunk
      sinceFrameRef.current += chunk.length
      start()
    },
    [start]
  )

  const end = useCallback(() => {
    endedRef.current = true
    // A stream that closed while text is still queued keeps animating; the
    // loop settles itself once the buffer drains. Only a stream that ended
    // with nothing pending needs waking, to clear `isTyping`.
    if (fullRef.current.length > shownRef.current) start()
    else if (frameRef.current === null) setIsTyping(false)
  }, [start])

  const flush = useCallback(() => {
    stop()
    endedRef.current = true
    carryRef.current = 0
    rateRef.current = MIN_CHARS_PER_FRAME
    clearArrivals()
    shownRef.current = fullRef.current.length
    setText(fullRef.current)
    setIsTyping(false)
  }, [stop, clearArrivals])

  const reset = useCallback(() => {
    stop()
    fullRef.current = ''
    shownRef.current = 0
    carryRef.current = 0
    rateRef.current = MIN_CHARS_PER_FRAME
    endedRef.current = false
    clearArrivals()
    setText('')
    setIsTyping(false)
  }, [stop, clearArrivals])

  // A pending frame outlives the component otherwise, and its setState would
  // fire against an unmounted tree.
  useEffect(() => stop, [stop])

  // `push`/`end`/`flush`/`reset` are stable, so consumers that list the
  // returned object as a dependency are not invalidated on every render.
  return useMemo(
    () => ({ text, isTyping, push, end, flush, reset }),
    [text, isTyping, push, end, flush, reset]
  )
}
